/**
 * Which nav entries a signed-in user is shown.
 *
 * Extracted from AppLayout so the rule can be tested directly: it is cosmetic,
 * but getting it wrong is highly visible — treating a missing `permissions`
 * field as "holds nothing" blanked the sidebar for a TENANT_ADMIN the moment
 * the web ran ahead of the API.
 */
export interface NavPrincipal {
  roles?: string[]
  permissions?: string[]
}

/**
 * Product decision: a collection agent's workspace is the mobile app. Their
 * permissions alone would still leave them Cases, Collections and Plan a trip
 * on web, so web is suppressed for them explicitly rather than by pretending
 * they lack the permissions — they hold them, and they use them on mobile.
 *
 * This is the one deliberate role-name test in the nav. It is keyed on the
 * user holding NO role other than COLLECTION_AGENT, so a dual-role
 * telecaller/field agent keeps the telecalling surfaces.
 */
export function isFieldOnly(roles: string[] | undefined): boolean {
  return !!roles && roles.length > 0 && roles.every((r) => r === 'COLLECTION_AGENT')
}

/**
 * Builds the `can(permission)` predicate the sidebar filters with.
 *
 * Three distinct states, and conflating any two of them shows the wrong nav:
 *
 *   me undefined         /me is still in flight. Nothing is shown yet, so the
 *                        sidebar fills in once rather than showing the full
 *                        list and retracting it — an entry that appears and
 *                        then vanishes reads as a bug.
 *
 *   permissions absent   An API older than this build: it does not publish the
 *                        field at all. Falls back to the ungated list. This
 *                        gate is cosmetic — every endpoint enforces itself —
 *                        so a version skew must leave a working user with too
 *                        much nav, never with none.
 *
 *   permissions present  Filtered against it. An empty array is a real answer
 *                        (the user holds nothing), not a skew, which is why
 *                        this tests the field's presence, not its length.
 */
export function navPermissionCheck(me: NavPrincipal | undefined): (permission: string) => boolean {
  if (!me) return () => false
  if (isFieldOnly(me.roles)) return () => false
  if (!Array.isArray(me.permissions)) return () => true

  const held = new Set(me.permissions)
  return (permission: string) => held.has(permission)
}
